import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Facebook, RefreshCw, Link2, Link2Off, BarChart2 } from "lucide-react";

/**
 * Facebook Lead Ads integration — Settings panel.
 *
 *   - Connect / Disconnect Facebook Business (Meta) Page
 *   - Show page name + status + last-synced timestamp
 *   - Ad performance table (Campaign / Spend / Leads / CPL / Status)
 *   - Manual "Sync Leads Now"
 *
 * Never renders access tokens. Every response from the backend already
 * strips them.
 */
export default function FacebookIntegrationPanel() {
  const [status, setStatus] = useState(null);
  const [perf, setPerf] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const s = await api.get("/integrations/facebook/status");
      setStatus(s.data);
      if (s.data?.connected) {
        try {
          const p = await api.get("/integrations/facebook/ad-performance?days=30");
          setPerf(p.data?.campaigns || []);
        } catch (e) { /* ad account may not be linked yet */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Facebook status");
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/facebook/auth");
      window.location.assign(data.oauth_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start Facebook OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Facebook? Lead Ads will stop flowing until you reconnect.")) return;
    setBusy(true);
    try {
      await api.post("/integrations/facebook/disconnect");
      toast.success("Facebook disconnected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/facebook/leads/sync");
      toast.success(`Synced — ${data.inserted} new, ${data.deduped} deduped`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  if (!status) return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8 text-sm text-zinc-500" data-testid="fb-panel-loading">
      Loading Facebook integration…
    </div>
  );

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8" data-testid="facebook-integration-panel">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
          <Facebook className="w-5 h-5 text-blue-600"/>
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-xl font-bold">Facebook Lead Ads</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Auto-import leads from Facebook Lead Ads, backup-sync via Graph API, and see campaign performance.
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest font-mono-accent px-2 py-1 rounded shrink-0 ${
            status.connected ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-700"
          }`}
          data-testid="fb-status-badge"
        >
          {status.connected ? "Connected" : "Not connected"}
        </span>
      </div>

      {!status.app_configured && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="fb-app-warning">
          Facebook App ID / Secret not configured on the server.
        </div>
      )}

      {status.connected ? (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="border border-zinc-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Page</div>
            <div className="font-semibold" data-testid="fb-page-name">{status.page_name || "—"}</div>
            <div className="text-xs text-zinc-500 font-mono-accent">ID: {status.page_id || "—"}</div>
          </div>
          <div className="border border-zinc-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Ad account</div>
            <div className="font-semibold font-mono-accent" data-testid="fb-ad-account">{status.ad_account_id || "—"}</div>
            <div className="text-xs text-zinc-500">
              Connected: {status.connected_at ? new Date(status.connected_at).toLocaleString() : "—"}
            </div>
          </div>
          <div className="border border-zinc-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Token expires</div>
            <div className="text-sm" data-testid="fb-token-expires">
              {status.token_expires_at ? new Date(status.token_expires_at).toLocaleString() : "—"}
            </div>
          </div>
          <div className="border border-zinc-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Last synced</div>
            <div className="text-sm" data-testid="fb-last-synced">
              {status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : "never"}
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6 rounded-lg border border-zinc-200 p-4 text-sm text-zinc-500">
          Connect your Facebook Business account to start ingesting Lead Ads leads.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {status.connected ? (
          <>
            <Button onClick={syncNow} disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="fb-sync-btn">
              <RefreshCw className="w-4 h-4 mr-1"/> Sync leads now
            </Button>
            <Button onClick={disconnect} disabled={busy} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="fb-disconnect-btn">
              <Link2Off className="w-4 h-4 mr-1"/> Disconnect
            </Button>
          </>
        ) : (
          <Button onClick={connect} disabled={busy || !status.app_configured} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="fb-connect-btn">
            <Link2 className="w-4 h-4 mr-1"/> Connect Facebook
          </Button>
        )}
      </div>

      {/* Webhook status */}
      <div className="mb-6 border border-zinc-200 rounded-lg p-3 text-xs text-zinc-600">
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-block w-2 h-2 rounded-full ${status.verify_token_configured ? "bg-emerald-500" : "bg-amber-500"}`}/>
          Webhook verify token {status.verify_token_configured ? "configured" : "missing"}
        </div>
        <div className="font-mono-accent" data-testid="fb-webhook-endpoint">
          Endpoint: <span className="bg-zinc-100 px-1 rounded">{status.webhook_endpoint}</span> · Graph {status.graph_api_version}
        </div>
      </div>

      {/* Ad performance */}
      {status.connected && (
        <div className="border border-zinc-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart2 className="w-4 h-4 text-zinc-500"/>
            <div className="font-medium">Ad performance (last 30 days)</div>
          </div>
          {perf.length === 0 ? (
            <div className="text-xs text-zinc-400">No campaign data yet — either the ad account has no active spend, or Ads Insights hasn't reported for this window.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fb-perf-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                    <th className="py-2">Campaign</th>
                    <th className="py-2 text-right">Spend</th>
                    <th className="py-2 text-right">Impr.</th>
                    <th className="py-2 text-right">Clicks</th>
                    <th className="py-2 text-right">Leads</th>
                    <th className="py-2 text-right">CPL</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {perf.map(c => (
                    <tr key={c.campaign_id} className="border-b border-zinc-100" data-testid={`fb-campaign-${c.campaign_id}`}>
                      <td className="py-2 pr-3">{c.campaign_name}</td>
                      <td className="py-2 text-right font-mono-accent">${c.spend?.toFixed?.(2) ?? "0.00"}</td>
                      <td className="py-2 text-right font-mono-accent">{c.impressions}</td>
                      <td className="py-2 text-right font-mono-accent">{c.clicks}</td>
                      <td className="py-2 text-right font-mono-accent">{c.leads}</td>
                      <td className="py-2 text-right font-mono-accent">{c.cpl != null ? `$${c.cpl}` : "—"}</td>
                      <td className="py-2 text-xs">{c.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
